import { useCallback, useEffect, useMemo, useState } from "react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import haversine from "haversine-distance";
import AuthenticatedWrapper from "../../../components/wrapper";
import IconButton from "../../../components/iconButton";
import variables from "../../../styles/utils.module.scss";
import Form from "react-bootstrap/Form";
import Map from "../../../components/map";
import { getGeocodeByPlaceId, getGeocodeWithArea, formatAddressWithoutStreet } from "../../../helpers/maps";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import axios from "axios";
import { fetcher, getParameterByName, serverUrl, swrConfig } from "../../../helpers/utility";
import { useForm } from "react-hook-form";
import { debounce, filter, sortBy } from "underscore";
import LocationSummary from "./LocationSummary";
import { Button, Row } from "react-bootstrap";
import LocationCard from "./LocationCard";
import useSWR from "swr";
import NavigationBack from "../../../components/NavigationBack";
import EnquiryModal from "./enquiryModal";

export default function Search({ user, locationsRaw, isGoogleMapLoaded }) {
  if (typeof user === "undefined" || typeof locationsRaw === "undefined") {
    console.log(`SKIP`);
    return <></>;
  }

  // AddPlacement parameters: placementId, placeId, placeName
  const placementId = getParameterByName("placementId");
  const newPlacement = getParameterByName("newPlacement");
  const lat = getParameterByName("lat");
  const lng = getParameterByName("lng");
  const [locations, setLocations] = useState(locationsRaw);
  const [educationAddress, setEducationAddress] = useState(null);
  const [location, setLocation] = useState(null);
  const [viewMode, setViewMode] = useState(`MAP`);
  const [place, setPlace] = useState(null);
  const [info, setInfo] = useState(null);
  const [routePolyline, setRoutePolyline] = useState(null);
  const [viewEnquiryModal, setViewEnquiryModal] = useState(false);
  const [enquiryModalLocation, setEnquiryModalLocation] = useState(null);
  const [driveDuration, setDriveDuration] = useState(null);
  const isLoaded = isGoogleMapLoaded;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    reset,
  } = useForm({
    defaultValues: {
      available: true,
      regulated: false,
      preferred: false,
      providerName: "",
    },
  });
  const formValues = watch();

  useEffect(() => {
    if (placementData?.filteredLocations) {
      debouncedClientSideFilter();
    }
    
  }, [formValues?.providerName, formValues?.regulated, formValues?.available]);

  const debouncedClientSideFilter = debounce(clientSideFilterHelper, 1000);

  const {
    data: placementData,
    error: placementError,
    isLoading: placementIsLoading,
  } = useSWR(
    placementId ? `${serverUrl}/placement/id/${placementId}?includeFilteredLocations=true` : null,
    fetcher,
    swrConfig
  );

  useEffect(() => {
    if (placementData?.placement) {
      const placement = placementData?.placement;

      // setLocation load is actually handled by setting the 'Place', since we have a useEffect that fires based off the change
      try {
        if (placement.location) {
          // Format the label to show only area, city, and postcode
          const formatPlaceLabel = async () => {
            if (placement.location?.placeId && isGoogleMapLoaded) {
              try {
                const geocodeResult = await geocodeByPlaceId(placement.location.placeId);
                const formattedLabel = formatAddressWithoutStreet(geocodeResult[0]);
                setPlace({
                  label: formattedLabel || placement.location?.label,
                  value: {
                    place_id: placement.location?.placeId,
                  },
                });
              } catch (error) {
                console.error(`Error formatting place label: ${error}`);
                setPlace({
                  label: placement.location?.label,
                  value: {
                    place_id: placement.location?.placeId,
                  },
                });
              }
            } else {
              setPlace({
                label: placement.location?.label,
                value: {
                  place_id: placement.location?.placeId,
                },
              });
            }
          };
          formatPlaceLabel();
        }
      } catch (error) {
        console.error(`Error setting new Place from Placement location. ${error}`);
      }

      if (placement.educationAddress) {
        setEducationAddress({
          label: placement.educationAddress.label,
          lat: placement.educationAddress.lat,
          lng: placement.educationAddress.lng,
          placeId: placement.educationAddress.placeId,
        });
      }
    }

    if (placementData?.filteredLocations) {
      debouncedClientSideFilter();
    }
  }, [placementData, isGoogleMapLoaded]);

  async function clientSideFilterHelper() {
    console.log(`Fired`);
    
    // Check if we have any locations to filter
    if (!placementData?.filteredLocations || placementData.filteredLocations.length === 0) {
      console.log('WARNING: No filteredLocations received from server');
      setLocations([]);
      return;
    }
    
    // Any additional client side filtering, e.g. on text values
    const clientSideFilteredLocations = clientSideFilter({
      locations: placementData?.filteredLocations,
      filters: formValues,
    });

    setLocations(clientSideFilteredLocations);

    if(newPlacement) {
      const now = new Date();
      const periodKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      await axios.put(`${serverUrl}/insight/${user.localAuthorityId}`, {
        localAuthorityId: user.localAuthorityId,
        periodKey: periodKey,
        periodType: "monthly",
        successfulMatches: clientSideFilteredLocations.filter((location) => {
          return (
            location.match?.available === 100 &&
            location.match?.gender === 100 &&
            location.match?.ageInMonths === 100 &&
            location.match?.filters === 100
          );
        }).length,
      });
    }
  }

  const locationsParsed = useMemo(() => {
    if (!location || typeof locations === "undefined" || locations?.length === 0) {
      return [];
    }


    return locations.map((locationItem) => {
      const placementDistance = location ? getMiles(
        haversine(
          {
            latitude: location.lat,
            longitude: location.lng,
          },
          {
            latitude: locationItem?.address?.lat,
            longitude: locationItem?.address?.lng,
          }
        )
      ) : null;

      const educationDistance = educationAddress ? getMiles(
        haversine(
          {
            latitude: educationAddress.lat,
            longitude: educationAddress.lng,
          },
          {
            latitude: locationItem?.address?.lat,
            longitude: locationItem?.address?.lng,
          }
        )
      ) : null;

      return {
        ...locationItem,
        position: {
          lat: locationItem?.address?.lat,
          lng: locationItem?.address?.lng,
        },
        distance: placementDistance,
        educationDistance: educationDistance,
        key: `${locationItem?._id}`,
      };
    });
  }, [place?.value?.place_id, location?.lat, location?.lng, locations, educationAddress?.lat, educationAddress?.lng]);

  function renderLocationList() {
    console.log(`renderLocationList: ${locationsParsed?.length}`);
    return sortBy(locationsParsed, "distance")?.map((location) => {
      console.log(JSON.stringify(location));
      return <LocationCard location={location} openEnquiryModal={openEnquiryModal} />;
    });
  }

  function getMiles(meters) {
    return (meters * 0.000621371192).toFixed(1);
  }

  async function getRouteToEducation(location) {
    try {
      const origin = `${location.address.addressLine1} ${location.address.townCity} ${location.address.postcode}`;
      const destination = educationAddress.label;

      const response = await axios.post(`${serverUrl}/location/route-to-education`, {
        origin,
        destination,
      });

      const { encodedPolyline, duration } = response.data;
      
      if (encodedPolyline && typeof encodedPolyline === 'string') {
        setRoutePolyline(encodedPolyline);
        setInfo({ ...location, driveDuration: duration });
      } else {
        console.warn('No encoded polyline found in routes response');
        setRoutePolyline(null);
        setDriveDuration(null);
      }
    } catch (error) {
      console.error('Error fetching route to education:', error);
      setRoutePolyline(null);
      setDriveDuration(null);
    }
  }

  const onMarkerClicked = useCallback((location) => {
    setRoutePolyline(" ");
    getRouteToEducation(location);
    setInfo({ ...info, ...location });
  }, [routePolyline, educationAddress]);

  useEffect(() => {
    async function lookupPlace() {
      if (place?.value?.place_id) {
        const results = await getGeocodeByPlaceId({
          isLoaded: isGoogleMapLoaded,
          placeId: place?.value?.place_id,
        });

        setLocation({
          lat: results.lat,
          lng: results.lng,
        });
      }
    }

    lookupPlace();
  }, [place]);

  // Supports: name, regulated
  function clientSideFilter({ locations, filters }) {
    const filteredLocations = filter(locations, (location) => {
      let match = true;
      if (typeof filters.providerName === "string" && filters.providerName?.length > 1) {
        match = match && location.name?.toLowerCase().includes(filters.providerName?.toLowerCase());
      }

      if (filters.regulated === true) {
        match = match && typeof location.ofstedId === "string" && location.ofstedId?.length >= 6;
      }

      if (filters.available === true) {
        match = match && location.match?.available === 100;
      }

      return match;
    });

    return filteredLocations;
  }

  const openEnquiryModal = useCallback((location) => {
    setViewEnquiryModal(true);
    setEnquiryModalLocation(location);
  }, []);

  function closeModal() {
    setViewEnquiryModal(false);
    setEnquiryModalLocation(null);
  }

  if (!isLoaded) {
    return <></>;
  }

  return (
    <AuthenticatedWrapper user={user}>
      <Row>
        <div style={{ display: "flex", flexDirection: "row", padding: 10, paddingBottom: 0 }}>
          {/* FILTER MENU */}
          <div
            style={{
              flexDirection: "column",
              flexGrow: 0,
              flexBasis: 250,
              flexShrink: 0,
              paddingRight: 10,
              marginRight: 10,
              borderRight: `1px solid ${variables.lightGrey}`,
              backgroundColor: variables.pureWhite,
              marginBottom: 10,
            }}
            className="d-none d-lg-flex"
          >
            <div
              className={`d-flex flex-grow-1 flex-column`}
              style={{ overflowY: "scroll", paddingLeft: 15, marginTop: 15 }}
            >
              <div style={{ width: "100%", marginBottom: 15 }}>
                <h1 className="h4">Search</h1>

                <Form.Control
                  type="text"
                  placeholder="Provider name"
                  {...register("providerName")}
                  id="providerName"
                />
              </div>

              <h1 className="h4" style={{ marginBottom: 10 }}>
                Filters
              </h1>

              <div style={{ width: "100%", marginBottom: 15 }}>
                <Form.Check // prettier-ignore
                  type="switch"
                  id="custom-switch"
                  label="Available"
                  {...register("available")}
                />
              </div>

              <div style={{ width: "100%", marginBottom: 15 }}>
                <Form.Check // prettier-ignore
                  type="switch"
                  id="custom-switch"
                  label="Regulated"
                  {...register("regulated")}
                />
              </div>

              <div style={{ width: "100%", marginBottom: 15 }}>
                <Form.Check // prettier-ignore
                  type="switch"
                  id="custom-switch"
                  label="Preferred list"
                  {...register("preferred")}
                />
              </div>

              {/* Map Legend */}
              <div style={{ width: "100%", marginTop: 20, marginBottom: 15 }}>
                <h1 className="h5" style={{ marginBottom: 10 }}>
                  Map Legend
                </h1>

                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#34a853",
                      borderRadius: "50%",
                      marginRight: 8,
                      border: "2px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  ></div>
                  <span style={{ fontSize: 12 }}>Available placement</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#ff9800",
                      borderRadius: "50%",
                      marginRight: 8,
                      border: "2px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  ></div>
                  <span style={{ fontSize: 12 }}>Limited availability</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#f44336",
                      borderRadius: "50%",
                      marginRight: 8,
                      border: "2px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  ></div>
                  <span style={{ fontSize: 12 }}>Not available</span>
                </div>

                {educationAddress && (
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: "#4285f4",
                        borderRadius: "50%",
                        marginRight: 8,
                        border: "2px solid white",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      }}
                    ></div>
                    <span style={{ fontSize: 12 }}>Education address</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* MAIN CONTENT */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 100,
              overflowY: "scroll",
              // 62px important or end up with scroll bar
              height: "calc(100vh - 62px)",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                flexWrap: "wrap",
                marginBottom: 6,
              }}
            >
              <div style={{ flex: 1, flexBasis: 300, marginRight: 15 }}>
                <NavigationBack inline={true} />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  flexGrow: 0,
                  flexBasis: 400,
                  height: "100%",
                }}
                className="justify-content-md-end"
              >
                <div style={{ 
                    width: "max-content", 
                    margin: "0px 0px 0px", 
                    height: "100%", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    flexDirection: "row",
                    gap: 5,
                    textAlign: "right",
                  }}>
                  <span style={{ fontSize: 14, fontWeight: "bold" }}>Home postcode: </span>
                  {place?.value?.place_id ? (
                    <span style={{ fontSize: 14 }}>{place.label}</span>
                  ) : null}
                </div>

                <div className="d-none d-lg-flex">
                  <div
                    style={{
                      border: `0px solid ${variables.grey}`,
                      borderRightWidth: "1px",
                      marginRight: 10,
                      paddingLeft: 10,
                      height: 34,
                      marginTop: 2,
                      width: 0,
                    }}
                  ></div>

                  <div style={{ marginRight: 4 }}>
                    <IconButton
                      onClick={() => setViewMode("LIST")}
                      title={`List`}
                      iconName={`list`}
                      iconColor={variables.darkGrey}
                      active={viewMode === "LIST"}
                    />
                  </div>

                  <IconButton
                    onClick={() => setViewMode("MAP")}
                    title={`Map`}
                    iconName={`map`}
                    iconColor={variables.darkGrey}
                    active={viewMode === "MAP"}
                  />
                </div>
              </div>
            </div>

            {viewMode === `LIST` ? (
              <div style={{ display: "flex", flexDirection: "column" }}>{renderLocationList()}</div>
            ) : null}

            <div
              className="flex-column flex-md-row"
              style={{
                // Preferable to hide rather than destroy
                display: viewMode === `LIST` ? "none" : "flex",
                flexGrow: 1,
                flexWrap: "wrap",
                marginBottom: 15,
              }}
            >
              <Map
                isLoaded={isLoaded}
                center={location}
                locations={locationsParsed}
                educationAddress={educationAddress}
                onClick={onMarkerClicked}
                routePolyline={routePolyline}
              />

              <LocationSummary
                info={info}
                openEnquiryModal={openEnquiryModal}
                placementId={placementId}
              />
            </div>
          </div>
        </div>
      </Row>

      <EnquiryModal
        placement={placementData?.placement}
        show={viewEnquiryModal}
        location={enquiryModalLocation}
        close={closeModal}
        size={"lg"}
        user={user}
      />
    </AuthenticatedWrapper>
  );
}
